import { get, omit } from 'lodash';
import {
  type RestLossAversionWithOffer,
  type RestModalForwards,
} from '../contracts';
import {
  RestModalAction,
  type EventObject,
  type EventSyncObject,
} from '../events';
import { type FunnelEventQueue } from './funnel-events';
import { type FunnelSurvey } from './survey';

type LossActionQueueInput = {
  kind:
    'checkout' | 'accept_offer' | 'accept_and_action' | 'cancel' | 'nevermind';
  card: RestLossAversionWithOffer;
  action: RestModalForwards;
  syncData: Pick<EventObject, 'session' | 'version' | 'app_id'> & {
    survey_state: FunnelSurvey;
  };
  fallbackCtaId?: string;
};

/** Preserve loss-aversion payloads independently from modal submission shapes. */
export const createLossActionQueue = ({
  kind,
  card,
  action,
  syncData,
  fallbackCtaId,
}: LossActionQueueInput): FunnelEventQueue => {
  const form = {
    sync: action.sync,
    message: get(action, 'sync.message'),
    survey_state: syncData.survey_state,
  };
  const commonKeys = {
    ...omit(syncData, 'survey_state'),
    card_version: card.version,
    direction: 'forwards' as const,
    id: card.id || null,
    name: card.name || card.id || 'loss_aversion_card',
    cta_ids: action.cta_details_list?.map((cta) => cta.cta_id) ?? [
      fallbackCtaId,
    ],
    cta_id: action.cta_details_list?.[0]?.cta_id ?? fallbackCtaId,
  };
  const event: EventObject = {
    ...commonKeys,
    id: card.id || null,
    name: card.name || card.id || 'loss_aversion_card',
    event: RestModalAction[action.action],
    url: action.url,
    properties: { survey_state: syncData.survey_state },
  };
  const sync: EventSyncObject = {
    ...commonKeys,
    action: action.action,
    type: action.type,
    modal: action.modal,
    validation: action.validation,
    url: action.url,
    form,
    survey_state: syncData.survey_state,
  };
  switch (kind) {
    case 'checkout':
      return { syncs: [{ ...sync, isCheckoutProcessing: true }] };
    case 'accept_offer':
      return {
        synchronous: true,
        events: [{ ...event, event: RestModalAction.accepted_la_offer }],
        syncs: [{ ...sync, action: RestModalAction.accept_offer }],
      };
    case 'accept_and_action':
      return {
        synchronous: true,
        events: [{ ...event, event: RestModalAction.accepted_la_offer }, event],
        syncs: [{ ...sync, action: RestModalAction.accept_offer }, sync],
      };
    case 'cancel':
      return {
        synchronous: true,
        events: [{ ...event, event: 'cancel' }],
        syncs: [{ ...sync, url: action.url, action: RestModalAction.link }],
      };
    case 'nevermind':
      return {
        synchronous: true,
        events: [
          {
            session: syncData.session,
            version: syncData.version,
            app_id: syncData.app_id,
            id: card.id,
            name: card.name,
            event: 'nevermind',
            properties: { survey_state: syncData.survey_state },
          },
        ],
        syncs: [
          {
            session: syncData.session,
            version: syncData.version,
            app_id: syncData.app_id,
            id: card.id,
            name: card.name,
            direction: 'backwards',
            type: action.type,
            action: RestModalAction.link,
            url: action.url,
            validation: action.validation,
            form: action.sync,
            survey_state: syncData.survey_state,
          },
        ],
      };
  }
};
